"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * The family's root: its fonts and tokens (.vfam), and the motion every family page shares. The page says it
 * has loaded (the hero rises), headings and cards (.rv, .mascot) rise once as they come into view, a section
 * off screen holds its loops (.off), and a .mag button leans toward the pointer. With reduced motion, only
 * what the stylesheet shows still.
 */
export function FamilyRoot({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setLoaded(true));
    });

    const off = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) =>
          en.target.classList.toggle("off", !en.isIntersecting)
        ),
      { rootMargin: "200px 0px" }
    );
    el.querySelectorAll("section, footer").forEach((s) => off.observe(s));

    const reveal = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          reveal.unobserve(en.target);
          en.target.classList.add("in");
        }),
      { rootMargin: "0px 0px -8% 0px" }
    );
    el.querySelectorAll(".rv, .mascot").forEach((r) => reveal.observe(r));

    const leaners: Array<() => void> = [];
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!still && window.matchMedia("(hover: hover)").matches) {
      el.querySelectorAll<HTMLElement>(".mag").forEach((b) => {
        const lean = (e: MouseEvent) => {
          const r = b.getBoundingClientRect();
          b.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.18}px,${(e.clientY - r.top - r.height / 2) * 0.3}px)`;
        };
        const settle = () => {
          b.style.transform = "";
        };
        b.addEventListener("mousemove", lean);
        b.addEventListener("mouseleave", settle);
        leaners.push(() => {
          b.removeEventListener("mousemove", lean);
          b.removeEventListener("mouseleave", settle);
        });
      });
    }

    return () => {
      cancelAnimationFrame(frame);
      off.disconnect();
      reveal.disconnect();
      leaners.forEach((stop) => stop());
    };
  }, []);

  return (
    <div ref={root} className={loaded ? `${className} loaded` : className}>
      {children}
    </div>
  );
}
